'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { useTRPC } from '@/lib/trpc/provider';
import { useAuthStore } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Parent-only "leave a review" form. Renders nothing unless the logged-in
 * user is a parent. Eligibility (active + paid enrollment) is enforced
 * server-side — an ineligible parent gets a friendly inline message rather
 * than the form being hidden, so they understand why.
 */
export function CourseReviewForm({
  courseId,
  onSubmitted,
}: {
  courseId: string;
  onSubmitted: () => void;
}) {
  const t = useTranslations('public.enrollment.reviews');
  const trpc = useTRPC();
  const role = useAuthStore((s) => s.user?.role);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [body, setBody] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const submitMutation = useMutation(
    trpc.courseReview.submit.mutationOptions({
      onSuccess: () => {
        toast.success(t('thanksToast'));
        setRating(0);
        setBody('');
        setDisplayName('');
        setInlineError(null);
        // New review lands as `pending` (not publicly visible yet); the
        // parent's targeted list refetch is enough — no global invalidation.
        onSubmitted();
      },
      onError: (err) => {
        const code = (err.data as { serviceCode?: string } | undefined)?.serviceCode;
        if (code === 'NOT_ELIGIBLE') {
          setInlineError(t('notEligible'));
        } else if (code === 'DUPLICATE_REVIEW') {
          setInlineError(t('duplicate'));
        } else {
          setInlineError(err.message);
        }
      },
    })
  );

  if (role !== 'parent') return null;

  const canSubmit =
    rating >= 1 &&
    rating <= 5 &&
    body.trim().length >= 10 &&
    displayName.trim().length >= 2 &&
    !submitMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!canSubmit) return;
    submitMutation.mutate({
      courseId,
      rating,
      body: body.trim(),
      displayName: displayName.trim(),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border bg-card p-6 shadow-sm space-y-4"
    >
      <h3 className="text-lg font-semibold text-eifo-primary">{t('leaveReview')}</h3>

      <div>
        <label className="mb-1 block text-sm font-medium">{t('yourRating')}</label>
        <div className="flex gap-1" role="radiogroup" aria-label={t('ratingAria')}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={t('starAria', { count: n })}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="p-0.5"
            >
              <Star
                className={cn(
                  'h-7 w-7 transition-colors',
                  (hover || rating) >= n
                    ? 'fill-eifo-orange text-eifo-orange'
                    : 'text-gray-300'
                )}
              />
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="review-body" className="mb-1 block text-sm font-medium">
          {t('yourReview')}
        </label>
        <textarea
          id="review-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder={t('reviewPlaceholder')}
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-eifo-orange/40"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {body.trim().length < 10
            ? t('minChars')
            : `${body.length}/2000`}
        </p>
      </div>

      <div>
        <label
          htmlFor="review-display-name"
          className="mb-1 block text-sm font-medium"
        >
          {t('displayName')}
        </label>
        <input
          id="review-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={60}
          placeholder={t('displayNamePlaceholder')}
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-eifo-orange/40"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {t('displayNameHint')}
        </p>
      </div>

      {inlineError && (
        <p className="text-sm text-red-600" role="alert">
          {inlineError}
        </p>
      )}

      <Button
        type="submit"
        disabled={!canSubmit}
        className="bg-eifo-orange text-white hover:bg-eifo-orange/90"
      >
        {submitMutation.isPending ? t('submittingReview') : t('submitReview')}
      </Button>
    </form>
  );
}
